"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import FormInput from "../../../components/FormInput";
import FileInput from "../../../components/FileInput";
import Button from "../../../components/Button";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown";
import styles from "../../dashboard/css/dashboard.module.css";
import formStyles from "../css/editUser.module.css";
import {
  getUserByEmail as apiGetUserByEmail,
  updateUser as apiUpdateUser,
  getUserByEmployeeNumber as apiGetUserByEmployeeNumber,
  updateUserByEmployeeNumber as apiUpdateUserByEmployeeNumber,
  getRolesDetailed as apiGetRolesDetailed,
  getBranches as apiGetBranches,
  previewFrontShopCode,
  uploadUserDoc,
} from "../../../api/userManagement/userManagementApi";
import UserValidationModal from "../components/UserValidationModal";

export default function EditUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location?.state?.email;
  const employeeNumberFromState = location?.state?.employee_number;
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    employee_number: "",
    role: "",
    roleId: null,
    roleIds: [],
    branchcodes: [],
    id_number: "",
    dob: "",
    phone_number: "",
    home_address: "",
    email: "",
    password: "",
    license_number: "",
    certification: "",
    docurl: "",
    cert_doc: null,
    frontshopcode: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [idError, setIdError] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!emailFromState && !employeeNumberFromState) return;
      try {
        setLoading(true);

        const u = emailFromState
          ? await apiGetUserByEmail(emailFromState)
          : await apiGetUserByEmployeeNumber(employeeNumberFromState);
        setFormData({
          first_name: u.first_name || "",
          last_name: u.last_name || "",
          employee_number: u.employee_number || "",
          role: u.role || "",
          roleId: typeof u.roleId === "number" ? u.roleId : null,
          roleIds: Array.isArray(u.roleIds) ? u.roleIds : (typeof u.roleId === "number" ? [u.roleId] : []),
          branchcodes: Array.isArray(u.branchcodes) ? u.branchcodes : (u.branchcode ? [u.branchcode] : []),
          id_number: u.id_number || "",
          dob: u.dob ? u.dob.substring(0, 10) : "",
          phone_number: u.phone_number || "",
          home_address: u.home_address || "",
          email: u.email || "",
          password: "********",
          license_number: u.license_number || "",
          certification: u.certification || "",
          docurl: u.docurl || "",
          cert_doc: null,
          frontshopcode: u.frontshopcode || "",
        });

        const roleIds = Array.isArray(u.roleIds)
          ? u.roleIds
          : (typeof u.roleId === "number" ? [u.roleId] : []);
        const needsFrontShopPin = roleIds.some((rid) => [3, 8, 9].includes(Number(rid)));
        if (needsFrontShopPin && !u.frontshopcode) {
          try {
            const code = await previewFrontShopCode();
            const pin = (code || "").toString().replace(/\D/g, "").slice(0, 4);
            if (pin.length === 4) {
              setFormData((prev) => ({ ...prev, frontshopcode: pin }));
            }
          } catch (e) {
            console.error("Failed to preview front shop PIN", e);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [emailFromState, employeeNumberFromState]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const data = await apiGetRolesDetailed();
        setRoles(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      }
    };
    loadRoles();
  }, []);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const data = await apiGetBranches();
        setBranches(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setBranches([]);
      }
    };
    loadBranches();
  }, []);

  useEffect(() => {
    if (!formData.role || !Array.isArray(roles) || roles.length === 0) return;
    const matched = roles.find((r) => r.rolename === formData.role);
    if (matched && formData.roleId !== matched.roleid) {
      setFormData((prev) => ({ ...prev, roleId: matched.roleid }));
    }
  }, [roles, formData.role, formData.roleId]);

  useEffect(() => {
    if (!Array.isArray(formData.roleIds) || formData.roleIds.length === 0) return;
    const primaryRoleId = Number(formData.roleIds[0]);
    if (!Number.isInteger(primaryRoleId)) return;
    if (formData.roleId !== primaryRoleId) {
      const name = roles.find((r) => Number(r.roleid) === primaryRoleId)?.rolename || formData.role;
      setFormData((prev) => ({ ...prev, roleId: primaryRoleId, role: name }));
    }
  }, [formData.roleIds, formData.roleId, roles, formData.role]);

  const handleChange = (name) => (valueOrEvent) => {
    if (name === "cert_doc") {
      // For file inputs, always expect an event
      setFormData({ ...formData, [name]: valueOrEvent.target.files[0] });
    } else {
      // Handle both direct value (e.g., from Dropdown) and event (e.g., from FormInput)
      let value = valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent;
      if (name === "id_number") {
        // digits only, max 13
        value = String(value || "").replace(/\D/g, "").slice(0, 13);
        // Clear any previous backend ID error when user edits the field
        if (idError) setIdError("");
      }
      // Helper: parse SA ID -> YYYY-MM-DD with century inference
      const parseSaIdDob = (id) => {
        if (!/^[0-9]{13}$/.test(id)) return "";
        const yy = parseInt(id.slice(0, 2), 10);
        const mm = parseInt(id.slice(2, 4), 10);
        const dd = parseInt(id.slice(4, 6), 10);
        if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return "";
        const currentYear = new Date().getFullYear() % 100;
        const century = yy <= currentYear ? 2000 : 1900;
        const fullYear = century + yy;
        const iso = `${fullYear.toString().padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        if (d.getUTCFullYear() !== fullYear || (d.getUTCMonth() + 1) !== mm || d.getUTCDate() !== dd) return "";
        return iso;
      };

      if (name === "id_number") {
        const maybeDob = parseSaIdDob(value);
        if (maybeDob) {
          setFormData({ ...formData, [name]: value, dob: maybeDob });
          // Clear any inline error when a valid full ID is entered
          if (idError) setIdError("");
          return;
        }
        if (value.length === 0) {
          setFormData({ ...formData, [name]: "", dob: "" });
          if (idError) setIdError("");
          return;
        }
        if (value.length >= 4) {
          const mm = parseInt(value.slice(2, 4), 10);
          if (!(mm >= 1 && mm <= 12)) {
            setIdError("Invalid ID format: month must be between 01 and 12");
          } else if (value.length >= 6) {
            const dd = parseInt(value.slice(4, 6), 10);
            if (!(dd >= 1 && dd <= 31)) {
              setIdError("Invalid ID format: day must be between 01 and 31");
            } else if (idError) {
              // If month/day now valid but we previously showed an error, clear it
              setIdError("");
            }
          } else if (idError) {
            // Month is valid but day not complete yet; clear stale error
            setIdError("");
          }
        } else if (idError) {
          // Less than 4 digits, clear any previous format error
          setIdError("");
        }
      }
      if (name === "role") {
        const selected = roles.find((r) => r.rolename === value);
        const roleId = selected ? selected.roleid : null;
        setFormData((prev) => ({ ...prev, role: value, roleId, roleIds: roleId ? [roleId] : [] }));
        return;
      }
      if (name === "roleIds") {
        const nextRoleIds = Array.isArray(value) ? value : [];
        const primaryRoleId = nextRoleIds.length ? Number(nextRoleIds[0]) : null;
        const primaryRoleName = primaryRoleId
          ? (roles.find((r) => Number(r.roleid) === primaryRoleId)?.rolename || "")
          : "";

        const needsFrontShopPin = nextRoleIds.some((rid) => {
          const n = Number(rid);
          return n === 3 || n === 8 || n === 9;
        });

        setFormData((prev) => ({
          ...prev,
          roleIds: nextRoleIds.map((r) => Number(r)).filter((n) => Number.isInteger(n)),
          roleId: primaryRoleId,
          role: primaryRoleName,
        }));

        if (needsFrontShopPin) {
          if (!String(formData.frontshopcode || "").trim()) {
            (async () => {
              try {
                const code = await previewFrontShopCode();
                const pin = (code || "").toString().replace(/\D/g, "").slice(0, 4);
                setFormData((prev) => ({ ...prev, frontshopcode: pin }));
              } catch (e) {
                console.error("Failed to preview front shop PIN", e);
                setFormData((prev) => ({ ...prev, frontshopcode: "" }));
              }
            })();
          }
        } else {
          setFormData((prev) => ({ ...prev, frontshopcode: "" }));
        }
        return;
      }
      if (name === "branchcodes") {
        const nextCodes = Array.isArray(value) ? value : [];
        const cleaned = Array.from(new Set(nextCodes.map((c) => String(c || "").trim()).filter(Boolean)));
        setFormData((prev) => ({ ...prev, branchcodes: cleaned }));
        return;
      }
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    (async () => {
      try {
        setSaving(true);
        const payload = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          employee_number: formData.employee_number,
          role: (formData.role || "").trim(),
          roleIds: Array.isArray(formData.roleIds) ? formData.roleIds : [],
          branchcodes: Array.isArray(formData.branchcodes) ? formData.branchcodes : [],
          branchcode: Array.isArray(formData.branchcodes) && formData.branchcodes.length ? formData.branchcodes[0] : undefined,
          id_number: formData.id_number,
          dob: formData.dob,
          phone_number: formData.phone_number,
          home_address: formData.home_address,
          license_number: formData.license_number,
          certification: formData.certification,
        };

        const needsFrontShopPin = (Array.isArray(formData.roleIds) ? formData.roleIds : []).some((rid) =>
          [3, 8, 9].includes(Number(rid))
        );
        if (needsFrontShopPin) {
          payload.frontshopcode = formData.frontshopcode;
        }

        // Upload certification doc if provided; set payload.docurl to uploaded URL
        if (formData.cert_doc && formData.employee_number) {
          try {
            const url = await uploadUserDoc(formData.employee_number, formData.cert_doc);
            if (url) payload.docurl = url;
          } catch (uploadErr) {
            console.error("User doc upload failed:", uploadErr);
          }
        }
        if (
          typeof formData.password === "string" &&
          formData.password.trim().length > 0 &&
          formData.password !== "********"
        ) {
          payload.password = formData.password;
        }

        if (emailFromState || formData.email) {
          await apiUpdateUser(emailFromState || formData.email, payload);
        } else if (employeeNumberFromState || formData.employee_number) {
          await apiUpdateUserByEmployeeNumber(
            employeeNumberFromState || formData.employee_number,
            payload
          );
        } else {
          throw new Error("No identifier (email or employee number) to update user");
        }
        navigate(-1);
      } catch (e) {
        console.error(e);
        // Surface validation/message from backend (e.g. SA ID validation)
        const resp = e?.response;
        const data = resp?.data;
        if (
          resp?.status === 400 &&
          data &&
          (typeof data?.error === "string" && data.error.toLowerCase().includes("invalid id number") ||
           typeof data?.reason === "string")
        ) {
          // For backend SA ID failures, behave like AddUser: show inline under ID field
          setIdError(data.reason || data.error || "Invalid ID number");
        } else {
          const backendMsg =
            (typeof data?.reason === "string" && data.reason) ||
            (typeof data?.error === "string" && data.error) ||
            e?.message ||
            "Failed to update user";
          setErrorMessages([backendMsg]);
          setErrorOpen(true);
        }
      } finally {
        setSaving(false);
      }
    })();
  };
  const roleOptionsDetailed = roles.map((r) => ({ value: r.roleid, label: r.rolename }));
  const branchOptionsDetailed = branches.map((b) => ({ value: b.branchcode, label: b.branchname }));

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <main className={styles.content}>
          <h1 className={formStyles.pageTitle}>Edit User</h1>

          <form className={formStyles.formContainer} onSubmit={handleSubmit}>
            {/* --- First Row --- */}
            <div className={formStyles.formRow}>
              <FormInput
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange("first_name")}
              />
              <FormInput
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange("last_name")}
              />
              <FormInput
                label="Employee Number"
                name="employee_number"
                value={formData.employee_number}
                onChange={handleChange("employee_number")}
              />
              <MultiSelectDropdown
                label="Role"
                name="roleIds"
                options={roleOptionsDetailed}
                value={formData.roleIds}
                onChange={handleChange("roleIds")}
                placeholder="Select Role(s)"
                maxVisible={3}
              />
              <MultiSelectDropdown
                label="Branch"
                name="branchcodes"
                options={branchOptionsDetailed}
                value={Array.isArray(formData.branchcodes) ? formData.branchcodes : []}
                onChange={handleChange("branchcodes")}
                placeholder="Select Branch(es)"
                maxVisible={3}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

            {/* --- Second Row --- */}
            <div className={formStyles.formRow}>
              <div>
                <FormInput
                  label="ID Number"
                  name="id_number"
                  value={formData.id_number}
                  onChange={handleChange("id_number")}
                />
                {idError && (
                  <small style={{ color: "#b00020", display: "block", marginTop: 4 }}>{idError}</small>
                )}
              </div>
              {(Array.isArray(formData.roleIds) ? formData.roleIds : []).some((rid) => [3, 8, 9].includes(Number(rid))) && (
                <FormInput
                  label="Front Shop PIN"
                  name="frontshopcode"
                  value={formData.frontshopcode}
                  onChange={handleChange("frontshopcode")}
                  disabled
                />
              )}
              <FormInput
                label="Date of Birth"
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange("dob")}
                disabled
              />
              <FormInput
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange("phone_number")}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

            {/* --- Third Row --- */}
            <div className={formStyles.formRow}>
              <FormInput
                label="Home Address"
                name="home_address"
                value={formData.home_address}
                onChange={handleChange("home_address")}
              />
              {formData.roleId !== 9 && (
                <FormInput
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                />
              )}
              {formData.roleId !== 9 && (
                <FormInput
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange("password")}
                />
              )}
              <FormInput
                label="License/Reg Number"
                name="license_number"
                value={formData.license_number}
                onChange={handleChange("license_number")}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

          {/* --- Certification Section --- */}
          <div className={formStyles.formRow}>
            <FormInput
              label="Certification"
              name="certification"
              value={formData.certification}
              onChange={handleChange("certification")}
            />
            <div className={formStyles.fileDisplay}>
              <FileInput
                label="Certification (PDF/Image)"
                name="cert_doc"
                id="cert_doc"
                value={formData.cert_doc}
                onChange={handleChange("cert_doc")}
                accept=".pdf,.jpg,.jpeg,.png"
                wide={true}
                existingUrl={formData.docurl}
              />
            </div>
          </div>
          {/* Action Buttons */}
          <div className={formStyles.formActions}>
            <Button variant="primary" type="submit" disabled={saving || loading}>Save</Button>
            <Button variant="secondary" type="button" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
          </div>
        </form>
      </main>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
    <UserValidationModal
      isOpen={errorOpen}
      onClose={() => setErrorOpen(false)}
      messages={errorMessages}
    />
  </div>
  );
}